import styles from './Modals.module.css'
import { AuditConfig } from '../../../../global/context/audit-config-context/AuditConfigContext'
import { AI_DECISION } from '../../../../global/constants/audit'

interface LookupAuditModalProps {
  lookedUpAudit: any
  config: AuditConfig | null
  onClose: () => void
  onImageClick: (url: string) => void
  formatSectionTitle: (key: string) => string
  getHumanDefects: (humanResponse: any) => string
}

function LookupAuditModal({
  lookedUpAudit,
  config,
  onClose,
  onImageClick,
  formatSectionTitle,
  getHumanDefects,
}: LookupAuditModalProps) {
  return (
    <div
      className={styles.failedExamplesOverlay}
      onClick={onClose}
    >
      <div
        className={styles.failedExamplesModal}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={styles.failedExamplesHeader}>
          <h2>Audit: {lookedUpAudit.audit.objectId}</h2>
          <button
            className={styles.lightboxClose}
            onClick={onClose}
          >
            ×
          </button>
        </div>
        <div className={styles.lookedUpAuditMeta}>
          <p>
            <strong>Type:</strong> {lookedUpAudit.audit.status}
          </p>
          <p>
            <strong>Date:</strong>{' '}
            {new Date(lookedUpAudit.audit.operationDate).toLocaleDateString()}
          </p>
          <p>
            <strong>Progress:</strong>{' '}
            {lookedUpAudit.audit.progress || 'Not started'}
          </p>
          {lookedUpAudit.audit.assignedToAudit && (
            <p>
              <strong>Assigned To:</strong>{' '}
              {lookedUpAudit.audit.assignedToAudit}
            </p>
          )}
          {lookedUpAudit.audit.assignedToCoach && (
            <p>
              <strong>Coach:</strong> {lookedUpAudit.audit.assignedToCoach}
            </p>
          )}
        </div>
        <div className={styles.failedExamplesContent}>
          {lookedUpAudit.sections.map((section: any, idx: number) => {
            // Get images for this section
            const sectionValue = config?.SECTIONS[section.sectionKey]
            if (!sectionValue) return null

            const sectionImageTypes = config?.IMAGE_TYPES?.[sectionValue]
            const imageTypesToMatch = Array.isArray(sectionImageTypes)
              ? sectionImageTypes
              : [sectionImageTypes]

            // Filter images that match this section's types
            const sectionImages = lookedUpAudit.images.filter((img: any) =>
              imageTypesToMatch.includes(img.type),
            )

            return (
              <div
                key={idx}
                className={styles.lookupSectionCard}
              >
                <h3>{formatSectionTitle(section.sectionKey)}</h3>

                {/* Section Images */}
                {sectionImages.length > 0 && (
                  <div className={styles.cardImagesSection}>
                    {sectionImages.map((img: any, imgIdx: number) => (
                      <img
                        key={imgIdx}
                        src={img.url}
                        alt={img.type}
                        className={styles.cardImage}
                        onClick={() => onImageClick(img.url)}
                      />
                    ))}
                  </div>
                )}

                {/* Human Response */}
                {section.humanResponse ? (
                  (() => {
                    const hasFail = Object.values(section.humanResponse).some(
                      (v: any) => v === true,
                    )
                    const decision = (
                      hasFail ? AI_DECISION.fail : AI_DECISION.pass
                    ).toUpperCase()
                    return (
                      <div
                        className={`${styles.decisionBlock} ${
                          styles[
                            `decision${
                              decision.charAt(0).toUpperCase() +
                              decision.slice(1).toLowerCase()
                            }`
                          ]
                        }`}
                      >
                        <div className={styles.decisionHeader}>
                          <strong>Human Decision:</strong>
                          <span className={styles.decisionBadge}>
                            {decision}
                          </span>
                        </div>
                        <div className={styles.decisionDetails}>
                          <p>
                            <strong>Defects:</strong>{' '}
                            {getHumanDefects({
                              response: section.humanResponse,
                            })}
                          </p>
                        </div>
                      </div>
                    )
                  })()
                ) : (
                  <p className={styles.noResponse}>
                    No human response recorded
                  </p>
                )}

                {/* Coaching Notes */}
                {section.coaching && section.coaching.notes && (
                  <div className={styles.coachingBlock}>
                    <strong>Coaching Notes:</strong>
                    <p>{section.coaching.notes}</p>
                  </div>
                )}

                {/* AI Review */}
                {section.aiReview && (
                  <div
                    className={`${styles.decisionBlock} ${
                      styles[
                        `decision${
                          section.aiReview.decision.charAt(0).toUpperCase() +
                          section.aiReview.decision.slice(1)
                        }`
                      ]
                    }`}
                  >
                    <div className={styles.decisionHeader}>
                      <strong>AI Review:</strong>
                      <span className={styles.decisionBadge}>
                        {section.aiReview.decision.toUpperCase()}
                      </span>
                    </div>
                    <div className={styles.decisionDetails}>
                      <p>
                        <strong>Reasoning:</strong> {section.aiReview.reasoning}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

export default LookupAuditModal
